import React, { useState, useContext, useEffect, useImperativeHandle, useRef } from "react";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";

import { useForm, Controller } from "react-hook-form";
import { ErrorMessage } from "@hookform/error-message";
import { AccountContext } from "context/account";

import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import SuiSelect from "components/SuiSelect";
import SuiInput from "components/SuiInput";
import SuiButton from "components/SuiButton";
import SuiTagInput from "components/SuiTagInput";

import Grid from "@mui/material/Grid";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import NumbersIcon from "@mui/icons-material/Numbers";
import EditIcon from "@mui/icons-material/Edit";
import UploadIcon from "@mui/icons-material/Upload";
import FullscreenIcon from "@mui/icons-material/Fullscreen";

import toast from "react-hot-toast";

import CategoryContext from "context/category-context";

import api from "services/api";
import moment from "moment";
import { capitalCase } from "capital-case";

import { Upload } from "antd";

import {
  uwProductClosureTypeOptions,
  uwProductCrownShapeOptions,
  uwProductVisorOptions,
} from "utils/data";
import ImageCardWrapper from "components/UpStyled/ImageCardWrapper";
import { handleUploadFiles, getStyleImages, canResizeImageUrl } from "utils/imageHelper";
import Tooltip from "@mui/material/Tooltip";

import Lightbox from "yet-another-react-lightbox";
import Zoom from "yet-another-react-lightbox/plugins/zoom";
import Download from "yet-another-react-lightbox/plugins/download";
import Thumbnails from "yet-another-react-lightbox/plugins/thumbnails";

const ProductInformationModal = ({
  visible,
  handleClose,
  handleOk,
  isEdit = false,
  product = {},
  materials = [],
  handleRefresh = () => {},
  handleAfterOk = () => {},
  refreshDelay = 0,
}) => {
  const [uploading, setUploading] = useState(false);
  const [images, setImages] = useState([]);
  const [selectedImage, setSelectedImage] = useState();
  const [editMode, setEditMode] = useState(isEdit);

  const [imgsViewerVisible, setImgsViewerVisible] = useState(false);
  const [imgsViewerCurrent, setImgsViewerCurrent] = useState(0);

  const [uwProductMaterialOptions, setUwProductMaterialOptions] = useState([]);

  const {
    register,
    handleSubmit,
    trigger,
    setValue,
    getValues,
    reset,
    watch,
    control,
    formState: { errors },
  } = useForm();

  useState(() => {
    const options = materials.map((x) => ({
      label: capitalCase(x.name, { stripRegexp: /[^A-Z0-9\'\&\/-_]/gi }),
      value: x.name,
    }));
    setUwProductMaterialOptions(options);
  }, []);

  const findOption = (content, key) => {
    if (!key || !content?.[key]) return undefined;

    let option = uwProductCrownShapeOptions.find((x) => x.value == content?.[key]);
    if (option?.value) {
      return option;
    }
    option = uwProductVisorOptions.find((x) => x.value == content?.[key]);
    if (option?.value) {
      return option;
    }

    option = uwProductClosureTypeOptions.find((x) => x.value == content?.[key]);
    if (option?.value) {
      return option;
    }

    option = uwProductMaterialOptions.find((x) => x.value == content?.[key]);
    if (option?.value) {
      return option;
    }

    return { value: content?.[key], label: content?.[key] };
  };

  useEffect(() => {
    if (product?.content?.images && product?.content?.images.length > 0) {
      setImages(product?.content?.images);
      setSelectedImage(product.content?.images[0]);
    }
  }, []);

  const TextComponent = ({ label, value }) => (
    <Grid items xs={12} sm={label == "description" ? 12 : 4}>
      <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
        <SuiTypography component="label" variant="caption" fontWeight="bold">
          {label}
        </SuiTypography>
        <SuiTypography variant="body1" color="secondary">
          {value}
        </SuiTypography>
      </SuiBox>
    </Grid>
  );

  const renderContent = (productContent) => {
    let keys = Object.keys(productContent);
    let data = [];
    for (let i = 0; i < keys.length; i++) {
      let value = productContent[keys[i]];
      if (typeof value == "string" || typeof value == "number") {
        let label = keys[i].replaceAll("_", " ");
        data.push({ label: label, value: value });
      }
    }

    return data.map((x) => <TextComponent label={x.label} value={x.value} />);
  };

  const openImgsViewer = () => setImgsViewerVisible(true);
  const closeImgsViewer = () => setImgsViewerVisible(false);

  const handleFullScreen = (e, selectedImage) => {
    const index = images.findIndex(
      (x) => x.url == selectedImage?.url || x.imgurl == selectedImage?.url
    );
    if (index > -1) {
      setImgsViewerCurrent(index);
      openImgsViewer();
    }
  };

  return (
    <Dialog
      open={visible}
      onClose={(e, reason) => {
        if (reason != "backdropClick") {
          handleClose();
        }
      }}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      maxWidth="lg"
      fullWidth={true}
    >
      <Lightbox
        open={imgsViewerVisible}
        close={closeImgsViewer}
        slides={images.map((x, index) => ({ src: x?.url || x?.imgurl, id: index }))}
        plugins={[Zoom, Download, Thumbnails]}
        zoom={{ maxZoomPixelRatio: 3 }}
        index={imgsViewerCurrent}
      />

      <DialogTitle id="alert-dialog-title">
        <SuiBox display="flex" gap={1}>
          {"Product Information"}
        </SuiBox>

        <IconButton
          aria-label="close"
          onClick={() => handleClose()}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <DialogContentText id="alert-dialog-description">
          <Grid container spacing={2}>
            <Grid item xs={12} sm={12} md={5}>
              <SuiBox>
                <SuiBox ml={0.5}>
                  <SuiBox display="flex" justifyContent="space-between" gap={1}>
                    <SuiTypography variant="button" fontWeight="bold" textTransform="capitalize">
                      Product Image
                    </SuiTypography>
                  </SuiBox>
                </SuiBox>
                <SuiBox px={4} py={1}>
                  <SuiBox
                    className="hover-parent"
                    sx={{ position: "relative", overflow: "hidden" }}
                  >
                    <SuiBox
                      className="hover-children"
                      sx={{
                        position: "absolute",
                        top: 4,
                        right: 4,
                        zIndex: (theme) => theme?.zIndex?.modal + 1,
                      }}
                    >
                      <Tooltip title={"Fullscreen"}>
                        <IconButton
                          size="small"
                          color="dark"
                          sx={{
                            backgroundColor: "#e3e3e3",
                            "&:hover": {
                              backgroundColor: "#a3a3a3",
                            },
                          }}
                          onClick={(e) => handleFullScreen(e, selectedImage)}
                        >
                          <FullscreenIcon />
                        </IconButton>
                      </Tooltip>
                    </SuiBox>
                    <ImageCardWrapper
                      data={selectedImage}
                      width={300}
                      height={300}
                      sx={{
                        borderRadius: "md",
                      }}
                    />
                  </SuiBox>

                  <SuiBox
                    sx={{ mt: 1, overflow: "auto" }}
                    width={"100%"}
                    display="flex"
                    flexWrap="noWrap"
                    gap={1}
                  >
                    {images.map((img) => (
                      <SuiBox sx={{ cursor: "pointer" }} onClick={() => setSelectedImage(img)}>
                        <ImageCardWrapper data={img} width={50} height={50} />
                      </SuiBox>
                    ))}
                  </SuiBox>
                </SuiBox>
              </SuiBox>
            </Grid>

            <Grid item xs={12} sm={12} md={7}>
              <Grid container spacing={1}>
                <Grid items xs={12} sm={8} md={8}>
                  <SuiBox pt={2} mb={1} ml={0.5} lineHeight={0} display="inline-block">
                    <SuiTypography component="label" variant="caption" fontWeight="bold">
                      Product Name
                    </SuiTypography>
                    <SuiTypography variant="body1" color="secondary">
                      {product.name}
                    </SuiTypography>
                  </SuiBox>
                </Grid>
                <Grid items xs={12} sm={4} md={4}>
                  <SuiBox pt={2} mb={1} ml={0.5} lineHeight={0} display="inline-block">
                    <SuiTypography component="label" variant="caption" fontWeight="bold">
                      Code
                    </SuiTypography>
                    <SuiTypography variant="body1" color="secondary">
                      {product.code}
                    </SuiTypography>
                  </SuiBox>
                </Grid>
                {renderContent(product.content)}
              </Grid>
            </Grid>
          </Grid>
          <SuiBox
            pt={3}
            px={3}
            display={"flex"}
            justifyContent="end"
            alignItems={{ xs: "flex-start", sm: "center" }}
            flexDirection={{ xs: "column", sm: "row" }}
          >
            <SuiBox display="flex" flexDirection={{ xs: "column", sm: "row" }}>
              <SuiBox ml={{ xs: 0, sm: 1 }} mt={{ xs: 1, sm: 0 }}>
                <SuiButton variant="gradient" size="medium" onClick={handleClose} color="light">
                  Cancel
                </SuiButton>
              </SuiBox>
            </SuiBox>
            <SuiBox display="flex" flexDirection={{ xs: "column", sm: "row" }}>
              <SuiBox ml={{ xs: 0, sm: 1 }} mt={{ xs: 1, sm: 0 }}>
                <SuiButton size="medium" variant="gradient" color="dark" type="submit">
                  Ok
                </SuiButton>
              </SuiBox>
            </SuiBox>
          </SuiBox>
        </DialogContentText>
      </DialogContent>
    </Dialog>
  );
};

export default ProductInformationModal;
